import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Trans } from '@lingui/react';
import React from 'react';
import {
  CustomReportRequirementStage,
  CustomReportRequirementStatusDto,
  CustomReportSubmissionStatus,
  CustomReportTargetType,
  useCustomReportRequirementsQuery
} from 'providers/api';
import CustomReportDocumentDialog from './CustomReportDocumentDialog';

const stageTitle = (stage: CustomReportRequirementStage) => {
  if (stage === CustomReportRequirementStage.BeforePourNoteApproval) return 'Approval required reports';
  return 'Completion required reports';
};

const requiredStageLabel = (stage?: CustomReportRequirementStage | undefined) => {
  if (stage === CustomReportRequirementStage.BeforePourNoteApproval) return 'Before pour note approval';
  return 'Before stope completion';
};

interface RequirementItemProps {
  projectId: string;
  requirement: CustomReportRequirementStatusDto;
}

const RequirementItem = ({ projectId, requirement }: RequirementItemProps) => {
  const [documentOpen, setDocumentOpen] = React.useState(false);
  const fields = requirement.template?.definition?.fields ?? [];
  const completed = requirement.submission?.status === CustomReportSubmissionStatus.Completed || requirement.isComplete;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
          {completed ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="warning" fontSize="small" />}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>{requirement.template?.name}</Typography>
            {requirement.template?.description && (
              <Typography variant="body2" color="text.secondary">{requirement.template.description}</Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              <Chip
                size="small"
                color={completed ? 'success' : 'warning'}
                label={completed ? 'Complete' : 'Required'}
                variant="outlined"
              />
              <Chip size="small" label={requiredStageLabel(requirement.stage)} variant="outlined" />
              {fields.length > 0 && <Chip size="small" label={`${fields.length} fields`} variant="outlined" />}
              {requirement.template?.sourceFile?.display && (
                <Chip
                  size="small"
                  icon={<DescriptionIcon />}
                  label={requirement.template?.sourceFile?.display}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        <Button
          variant={completed ? 'outlined' : 'contained'}
          color="secondary"
          startIcon={<EditIcon />}
          onClick={() => setDocumentOpen(true)}
          sx={{ justifySelf: { xs: 'stretch', md: 'end' } }}
        >
          {completed ? <Trans id="Open Report">Open Report</Trans> : <Trans id="Complete Report">Complete Report</Trans>}
        </Button>
      </Paper>
      <CustomReportDocumentDialog
        open={documentOpen}
        projectId={projectId}
        requirement={requirement}
        onClose={() => setDocumentOpen(false)}
      />
    </>
  );
};

interface CustomReportCompletionPanelProps {
  projectId: string;
  targetType: CustomReportTargetType;
  targetId: string;
  stage: CustomReportRequirementStage;
  title?: React.ReactNode;
  hideWhenEmpty?: boolean;
}

const CustomReportCompletionPanel = ({
  projectId,
  targetType,
  targetId,
  stage,
  title,
  hideWhenEmpty = false,
}: CustomReportCompletionPanelProps) => {
  const { data: requirements = [], isLoading } = useCustomReportRequirementsQuery(
    { projectId, targetType, targetId },
    { enabled: Boolean(projectId && targetId) },
  );

  const stageRequirements = requirements.filter((requirement) => requirement.stage === stage);
  const completeCount = stageRequirements.filter((requirement) => requirement.isComplete).length;

  if (hideWhenEmpty && !isLoading && stageRequirements.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title ?? stageTitle(stage)}
        </Typography>
        {stageRequirements.length > 0 && (
          <Chip
            size="small"
            label={`${completeCount}/${stageRequirements.length}`}
            color={completeCount === stageRequirements.length ? 'success' : 'warning'}
          />
        )}
      </Box>
      {!isLoading && stageRequirements.length === 0 && (
        <Alert severity="info">
          <Trans id="No custom reports are required for this step.">No custom reports are required for this step.</Trans>
        </Alert>
      )}
      <Stack spacing={1}>
        {stageRequirements.map((requirement) => (
          <RequirementItem
            key={`${requirement.template?.entityId}-${requirement.stage}`}
            projectId={projectId}
            requirement={requirement}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default CustomReportCompletionPanel;
